import { useCallback, useEffect, useMemo, useState } from 'react'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import toast from 'react-hot-toast'
import { createPaymentIntent, getStripe, getStripeForConnect, reportPaymentStatus } from '../services/stripeService'

const DEFAULT_STATUS = {
  state: 'idle',
  message: ''
}

const StripeCheckoutForm = ({
  clientSecret,
  returnUrl,
  metadata,
  onPaymentStatus,
  onPaymentElementReady,
  onPaymentElementError
}) => {
  const stripe = useStripe()
  const elements = useElements()
  const [status, setStatus] = useState(DEFAULT_STATUS)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [paymentElementReady, setPaymentElementReady] = useState(false)

  const notifyBackend = useCallback(
    async ({ paymentIntentId, status: paymentStatus, errorMessage }) => {
      try {
        await reportPaymentStatus({
          paymentIntentId,
          status: paymentStatus,
          errorMessage,
          metadata
        })
      } catch (error) {
        console.warn('Unable to report payment status.', error)
      }
    },
    [metadata]
  )

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!stripe || !elements || !paymentElementReady) {
      return
    }

    setIsSubmitting(true)
    setStatus({ state: 'processing', message: 'Confirming payment…' })

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl
        },
        redirect: 'if_required'
      })

      if (error) {
        const message = error.message || 'Payment could not be processed.'
        setStatus({
          state: 'error',
          message
        })
        toast.error(message)
        await notifyBackend({
          paymentIntentId: error.payment_intent?.id,
          status: 'failed',
          errorMessage: error.message
        })
        onPaymentStatus?.('failed', null)
        setIsSubmitting(false)
        return
      }

      if (paymentIntent) {
        const nextState =
          paymentIntent.status === 'succeeded' ? 'success' : 'processing'
        const nextMessage =
          paymentIntent.status === 'succeeded'
            ? 'Payment successful!'
            : `Payment ${paymentIntent.status}.`

        setStatus({ state: nextState, message: nextMessage })
        await notifyBackend({
          paymentIntentId: paymentIntent.id,
          status: paymentIntent.status
        })
        onPaymentStatus?.(paymentIntent.status, paymentIntent)
      }

      setIsSubmitting(false)
    } catch (error) {
      const message =
        error?.message || 'Payment could not be processed. Please try again.'
      setStatus({ state: 'error', message })
      toast.error(message)
      await notifyBackend({
        paymentIntentId: null,
        status: 'failed',
        errorMessage: error?.message
      })
      onPaymentStatus?.('failed', null)
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    const checkPaymentIntent = async () => {
      if (!stripe || !clientSecret) {
        return
      }

      try {
        const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret)
        if (!paymentIntent) {
          return
        }

        if (paymentIntent.status === 'succeeded') {
          setStatus({ state: 'success', message: 'Payment successful!' })
        }
      } catch (error) {
        const message =
          error?.message ||
          'Unable to confirm payment status. Please refresh the page.'
        setStatus({ state: 'error', message })
        toast.error(message)
      }
    }

    checkPaymentIntent()
  }, [clientSecret, stripe])

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement
        options={{ layout: 'tabs' }}
        onReady={() => {
          setPaymentElementReady(true)
          onPaymentElementReady?.()
        }}
        onLoadError={(event) => {
          console.error('[StripeCheckout] Payment Element loaderror', event)
          onPaymentElementError?.(event)
          setPaymentElementReady(false)
        }}
      />
      {status.message ? (
        <p
          className={`text-sm ${
            status.state === 'error' ? 'text-red-600' : 'text-gray-600'
          }`}
        >
          {status.message}
        </p>
      ) : null}
      <button
        type="submit"
        disabled={!stripe || !paymentElementReady || isSubmitting}
        className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
      >
        {isSubmitting ? 'Processing…' : 'Pay now'}
      </button>
    </form>
  )
}


const StripeCheckout = ({
  amount,
  currency = 'usd',
  description,
  customerEmail,
  metadata,
  returnUrl,
  onPaymentStatus,
  eventId,
  usePlatformAccount
}) => {
  const platformStripePromise = useMemo(() => getStripe(), [])
  const [clientSecret, setClientSecret] = useState('')
  const [stripeAccount, setStripeAccount] = useState(null)
  const [errorMessage, setErrorMessage] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isStripeAvailable, setIsStripeAvailable] = useState(null)

  const stripePromise = useMemo(
    () => (stripeAccount ? getStripeForConnect(stripeAccount) : platformStripePromise),
    [stripeAccount, platformStripePromise]
  )

  const computedReturnUrl = useMemo(() => {
    if (returnUrl) {
      return returnUrl
    }
    if (typeof window === 'undefined') {
      return ''
    }
    return `${window.location.origin}/payments/confirmation`
  }, [returnUrl])

  useEffect(() => {
    let isMounted = true

    const resolveStripe = async () => {
      try {
        const stripe = await platformStripePromise
        if (isMounted) {
          const available = Boolean(stripe)
          if (!available) {
            console.warn('[StripeCheckout] Stripe SDK unavailable (missing publishable key or load failed)')
          }
          setIsStripeAvailable(available)
        }
      } catch (error) {
        console.warn('[StripeCheckout] Unable to initialize Stripe.', error)
        if (isMounted) {
          setIsStripeAvailable(false)
        }
      }
    }

    resolveStripe()

    return () => {
      isMounted = false
    }
  }, [platformStripePromise])

  useEffect(() => {
    let isMounted = true

    const initPayment = async () => {
      const resolvedEventId = eventId || metadata?.eventId
      try {
        setIsLoading(true)
        console.log('[StripeCheckout] createPaymentIntent called', {
          amount,
          currency,
          eventId: resolvedEventId,
          usePlatformAccount,
          metadata
        })
        const response = await createPaymentIntent({
          amount,
          currency,
          metadata,
          customerEmail,
          description,
          eventId: resolvedEventId,
          usePlatformAccount
        })
        if (isMounted) {
          setClientSecret(response.clientSecret)
          setStripeAccount(response.stripeAccount ?? null)
          setErrorMessage('')
        }
      } catch (err) {
        const resolvedEventIdForLog = eventId || metadata?.eventId
        console.error('[StripeCheckout] createPaymentIntent failed', {
          message: err?.message,
          eventId: resolvedEventIdForLog,
          error: err
        })
        if (isMounted) {
          setErrorMessage(
            err?.message || 'Unable to initialize Stripe payment.'
          )
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    if (isStripeAvailable === false) {
      setClientSecret('')
      setErrorMessage('')
      setIsLoading(false)
    } else if (isStripeAvailable && amount) {
      initPayment()
    } else if (isStripeAvailable) {
      setIsLoading(false)
      setErrorMessage('Missing payment amount.')
    }

    return () => {
      isMounted = false
    }
  }, [amount, currency, customerEmail, description, isStripeAvailable, metadata, eventId, usePlatformAccount])

  if (isStripeAvailable === false) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Payments are temporarily unavailable. Please contact support.
          </p>
          <button
            type="button"
            disabled
            className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:bg-blue-300"
          >
            Pay now
          </button>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-sm text-gray-600">Preparing secure payment…</p>
      </div>
    )
  }

  if (errorMessage) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        {errorMessage}
      </div>
    )
  }

  if (!clientSecret) {
    return null
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret,
          appearance: { theme: 'stripe' }
        }}
      >
        <StripeCheckoutForm
          clientSecret={clientSecret}
          returnUrl={computedReturnUrl}
          metadata={metadata}
          onPaymentStatus={onPaymentStatus}
          onPaymentElementReady={() => console.log('[StripeCheckout] Payment Element mounted')}
          onPaymentElementError={(e) => console.error('[StripeCheckout] Payment Element loaderror', e)}
        />
      </Elements>
    </div>
  )
}

export default StripeCheckout
